/**
 * UI-8f Task 4 — useIncidents hook.
 *
 * Drives the six platform incident endpoints:
 *
 *   GET    /platform/incidents?status=&severity=
 *   POST   /platform/incidents
 *   GET    /platform/incidents/:id
 *   PATCH  /platform/incidents/:id
 *   POST   /platform/incidents/:id/transition
 *   POST   /platform/incidents/:id/notes
 *
 * The list is composed via `useAdminResource('/platform/incidents', { responseKey: 'incidents', query: filters })`
 * (the UI-7-0 alias for useAdminApi). The state-machine helpers
 * (transition + appendNote) and the single-row reader (getOne) sit outside
 * the standard CRUD envelope, so they are direct `api.post` / `api.get`
 * calls — mirroring the useTenants pattern from UI-8a.
 *
 * All routes are gated server-side by `PLATFORM_INCIDENTS_ADMIN`; the pages
 * (IncidentsPage + IncidentDetailPage) wrap themselves in
 * <RequirePermission> for the client gate.
 */
import { useCallback } from 'react'
import { api } from '../api/client.js'
import { useAdminResource } from './useAdminApi.js'

export interface Note {
  at: string
  by: string
  text: string
}

export interface Incident {
  id: string
  title: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  status: 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'CLOSED'
  description: string
  detectedAt: string
  resolvedAt: string | null
  responseActions: string | null
  lessonsLearned: string | null
  notes: Note[]
}

export interface CreateIncidentBody {
  title: string
  severity: string
  description: string
}

export interface UpdateIncidentBody {
  title?: string
  severity?: string
  description?: string
}

export interface TransitionPayload {
  to: string
  responseActions?: string
  lessonsLearned?: string
}

export interface UseIncidentsFilters {
  status?: string
  severity?: string
}

export interface UseIncidentsResult {
  items: Incident[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  create: (body: CreateIncidentBody) => Promise<Incident>
  update: (id: string, body: UpdateIncidentBody) => Promise<Incident>
  /** POST /platform/incidents/:id/transition — drives the state machine. */
  transition: (id: string, payload: TransitionPayload) => Promise<Incident>
  /** POST /platform/incidents/:id/notes — appends one note (no return body). */
  appendNote: (id: string, text: string) => Promise<void>
  /** GET /platform/incidents/:id — single-row reader for the detail page. */
  getOne: (id: string) => Promise<Incident>
}

const BASE_PATH = '/platform/incidents'

export function useIncidents(filters?: UseIncidentsFilters): UseIncidentsResult {
  const base = useAdminResource<Incident>(BASE_PATH, {
    responseKey: 'incidents',
    ...(filters ? { query: filters as Record<string, string | undefined> } : {}),
  })

  // useAdminResource.refetch() is fire-and-forget (bumps a fetchKey); wrap so
  // callers can `await` after a mutation. List state updates via the effect.
  const refetch = useCallback(async (): Promise<void> => {
    base.refetch()
  }, [base])

  const create = useCallback(
    async (body: CreateIncidentBody): Promise<Incident> => {
      const created = await api.post<Incident>(BASE_PATH, body)
      base.refetch()
      return created
    },
    [base],
  )

  const update = useCallback(
    async (id: string, body: UpdateIncidentBody): Promise<Incident> => {
      const updated = await api.patch<Incident>(`${BASE_PATH}/${id}`, body)
      base.refetch()
      return updated
    },
    [base],
  )

  const transition = useCallback(
    async (id: string, payload: TransitionPayload): Promise<Incident> => {
      return api.post<Incident>(
        `${BASE_PATH}/${id}/transition`,
        payload,
      )
    },
    [],
  )

  const appendNote = useCallback(
    async (id: string, text: string): Promise<void> => {
      await api.post(`${BASE_PATH}/${id}/notes`, { text })
    },
    [],
  )

  const getOne = useCallback(async (id: string): Promise<Incident> => {
    return api.get<Incident>(`${BASE_PATH}/${id}`)
  }, [])

  return {
    items: base.items,
    loading: base.loading,
    error: base.error,
    refetch,
    create,
    update,
    transition,
    appendNote,
    getOne,
  }
}
